import React, { useState, useEffect } from 'react';
import { CloseIcon, PlusIcon, DeleteICon } from '../../../public/icons';
import { useToast } from '../../../context/ToastContext';
import adminMemoirService from '../../../services/admin/adminMemoirService';

const AddQuestionsModal = ({ isOpen, onClose, categories, onQuestionsAdded }) => {
  const { success, error: showError } = useToast();
  const [formData, setFormData] = useState({
    category: '',
    questions: ['']
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [removingIndex, setRemovingIndex] = useState(null);

  useEffect(() => {
    if (isOpen && categories && categories.length > 0) {
      setFormData({
        category: categories[0]?._id || '',
        questions: ['']
      });
      setIsSubmitting(false);
      setRemovingIndex(null);
    }
  }, [isOpen, categories]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleQuestionChange = (index, value) => {
    const updatedQuestions = [...formData.questions];
    updatedQuestions[index] = value;
    setFormData(prev => ({
      ...prev,
      questions: updatedQuestions
    }));
  };

  const handleAddQuestion = () => {
    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, '']
    }));
  };

  const handleRemoveQuestion = async (index) => {
    setRemovingIndex(index);
    // Simulate a small delay for better UX
    setTimeout(() => {
      if (formData.questions.length <= 1) {
        setRemovingIndex(null);
        return;
      }
      
      const updatedQuestions = formData.questions.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        questions: updatedQuestions
      }));
      setRemovingIndex(null);
    }, 300);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      
      if (!formData.category) {
        showError('Please select a category');
        setIsSubmitting(false);
        return;
      }
      
      const nonEmptyQuestions = formData.questions.filter(q => q.trim() !== '');
      if (nonEmptyQuestions.length === 0) {
        showError('Please add at least one question');
        setIsSubmitting(false);
        return;
      }
      
      const questionsToSubmit = nonEmptyQuestions.map((q, index) => ({
        question: q.trim(),
        weekNumber: 1,
        order: index
      }));
      
      const response = await adminMemoirService.createQuestions({
        category: formData.category,
        questions: questionsToSubmit
      });
      
      success(response.message || 'Questions added successfully');
      
      if (onQuestionsAdded) {
        onQuestionsAdded(response.data);
      }
      
      onClose();
    } catch (error) {
      console.error('Error submitting questions:', error);
      showError(error.response?.data?.message || 'Failed to add questions. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitEnabled = formData.category && formData.questions.some(q => q.trim() !== '') && !isSubmitting;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[100] transition-opacity duration-300 text-left"
        onClick={onClose}
      />
      
      <div className="fixed right-0 top-0 h-full w-full md:w-[60%] lg:w-[50%] xl:w-[40%] bg-[#fff] z-[999] overflow-y-auto transition-transform duration-300 ease-in-out shadow-xl">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between pb-5 pt-4 border-b px-6 border-[#DBDBDB]">
            <div className="text-left">
              <h2 className="text-2xl font-bold text-[#1C2A46]">Add Questions</h2>
              <p className="text-[#6B635D] text-sm font-light mt-1">
                Create a guided prompt for memoir submissions
              </p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-[#F8F8F8] border border-[#DBDBDB] rounded-sm transition-colors"
              disabled={isSubmitting}
            >
              <CloseIcon className="w-5 h-5 text-[#1C2A46]" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 text-left">
            {/* Category Selection */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                Category <span className="text-[#C03A2B]">*</span>
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full p-3 border-2 border-[#E4E4E4] text-[#1C2A46] text-sm focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
                disabled={isSubmitting}
              >
                {categories && categories.length > 0 ? (
                  categories.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.name}
                    </option>
                  ))
                ) : (
                  <option value="">No categories available</option>
                )}
              </select>
            </div>

            {/* Add Questions Section */}
            <div className="mb-6">
              <div className="flex items-center mb-5 justify-between">
                <div>
                  <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                    Add Questions
                  </label>
                  <p className="text-xs text-[#6B635D]">
                    You can add multiple questions for a category
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleAddQuestion}
                  className="mt-2 flex items-center gap-2 text-[#55000C] transition-colors hover:text-[#8F2F84] disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  <PlusIcon size={16} color="#55000C" />
                  <span>Add</span>
                </button>
              </div>

              {formData.questions.map((question, index) => (
                <div key={index} className="mb-4">
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <label className="block text-xs text-[#6B635D] mb-1">
                        Question {index + 1} <span className="text-[#C03A2B]">*</span>
                      </label>
                      <input
                        type="text"
                        value={question}
                        onChange={(e) => handleQuestionChange(index, e.target.value)}
                        placeholder="Who was the most influential person in your early life?"
                        className="w-full p-3 border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
                        disabled={isSubmitting}
                      />
                    </div>
                    {formData.questions.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveQuestion(index)}
                        className="mt-6 p-2 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={isSubmitting || removingIndex === index}
                      >
                        {removingIndex === index ? (
                          <div className="w-5 h-5 border-2 border-[#C03A2B] border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <DeleteICon size={20} className="text-[#C03A2B]" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-[#DBDBDB]">
            <button 
              onClick={handleSubmit}
              disabled={!isSubmitEnabled}
              className={`w-full px-6 py-3 font-medium rounded-sm transition-colors flex items-center justify-center gap-2 ${
                isSubmitEnabled
                  ? 'bg-[#D2A146] text-white hover:bg-[#C49660]'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSubmitting && (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              {isSubmitting ? 'Adding Questions...' : 'Add Questions'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddQuestionsModal;